import React from 'react'
import { Link } from 'react-router-dom'
import EventOutlinedIcon from '@material-ui/icons/EventOutlined'
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import PhonelinkEraseOutlinedIcon from '@material-ui/icons/PhonelinkEraseOutlined'
import useSmsPolicyStyles from './smsPolicyStyles'

const messageTypes = [
	'One-time passcodes and identity-verification codes',
	'Security and account alerts',
	'Assignment notifications and updates',
	'Work-status changes',
	'Operational reminders',
	'Other internal, non-marketing work-related notices',
]

export default function SmsTerms() {
	const classes = useSmsPolicyStyles()

	return (
		<div className={classes.root}>
			<div className={classes.effectiveDate}>
				<div className={classes.effectiveIcon} aria-hidden="true">
					<EventOutlinedIcon />
				</div>
				<div>
					<p className={classes.effectiveTitle}>Effective Date: August 6, 2026</p>
					<p className={classes.effectiveBody}>
						These SMS Terms and Conditions govern participation in the Claim Consultant Group SMS program. By affirmatively opting in to receive SMS messages from Claim Consultant Group, LLC (“Claim Consultant Group,” “CCG,” “we,” “us,” or “our”), you acknowledge and agree to these terms.
					</p>
				</div>
			</div>

			<div className={classes.infoBanner} style={{ marginTop: 0, marginBottom: '2.75rem' }}>
				<InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
				<span>Message frequency varies. Message and data rates may apply. Reply STOP to cancel or HELP for help. Mobile numbers are not shared with third parties or affiliates for marketing or promotional purposes.</span>
			</div>

			<section className={classes.section}>
				<div className={classes.sectionHeader}>
					<div className={classes.sectionIcon} aria-hidden="true">
						<VerifiedUserOutlinedIcon />
					</div>
					<div>
						<h2 className={classes.sectionTitle}>Program Name and Purpose</h2>
						<span className={classes.accent} aria-hidden="true" />
					</div>
				</div>
				<p className={classes.body}>
					The Claim Consultant Group SMS Program provides one-time passcodes and non-marketing, work-related notifications to authorized CCG users (including employees and contractors).
				</p>
				<p className={classes.body}>Messages may include:</p>
				<ul className={classes.checkList}>
					{messageTypes.map((item) => (
						<li key={item} className={classes.checkItem}>
							<CheckCircleIcon className={classes.checkIcon} aria-hidden="true" />
							<span>{item}</span>
						</li>
					))}
				</ul>
				<div className={classes.infoBanner}>
					<InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />
					<span>The program is not used to send advertising, sales, or promotional messages.</span>
				</div>
			</section>

			<div className={classes.splitRow}>
				<div className={classes.splitCol}>
					<div className={classes.splitIcon} aria-hidden="true">
						<LockOutlinedIcon />
					</div>
					<h2 className={classes.splitTitle}>Your Consent</h2>
					<span className={classes.accent} aria-hidden="true" />
					<p className={classes.body} style={{ marginTop: '1rem' }}>
						Authorized CCG users (including employees and contractors) opt in at <a href="https://www.claimconsultantgroup.com/sms-opt-in">https://www.claimconsultantgroup.com/sms-opt-in</a> by entering their mobile telephone number and checking an unchecked consent box agreeing to receive the SMS messages described in these terms. This is the only opt-in method used for this campaign. Participation is voluntary and is not a condition of employment, engagement, or receipt of related benefits.
					</p>
					<p className={classes.body}>
						Consent is collected specifically for this SMS program. It is separate from acceptance of any other policy or agreement, is not bundled with unrelated agreements, and applies only to the mobile number provided. See our <Link to="/sms-opt-in">SMS Opt-In Process</Link> for the exact consent language.
					</p>
				</div>
				<div className={classes.splitDivider} aria-hidden="true" />
				<div className={classes.splitCol}>
					<div className={classes.splitIcon} aria-hidden="true">
						<PhonelinkEraseOutlinedIcon />
					</div>
					<h2 className={classes.splitTitle}>Opt-Out</h2>
					<span className={classes.accent} aria-hidden="true" />
					<p className={classes.body} style={{ marginTop: '1rem' }}>
						Reply <span className={classes.keyword}>STOP</span> to opt out of future Claim Consultant Group SMS messages.
					</p>
					<p className={classes.body}>
						After an opt-out request is processed, no further SMS messages will be sent unless the user later affirmatively opts in again. Opting out may prevent delivery of verification codes, security alerts, and other operational notifications.
					</p>
					<p className={classes.body}>
						Users who opt out should contact <a href="mailto:info@claimconsultantgroup.com">info@claimconsultantgroup.com</a> regarding available alternatives.
					</p>
				</div>
			</div>

			<section className={classes.legalSection}>
				<h2>Eligibility</h2>
				<p>The program is available to authorized CCG users (including employees and contractors) who:</p>
				<ul>
					<li>Provide a valid mobile telephone number;</li>
					<li>Are authorized to access the applicable CCG system or receive the applicable notification; and</li>
					<li>Affirmatively consent to receive the SMS messages described in these terms.</li>
				</ul>
				<p>Users must promptly notify Claim Consultant Group if their mobile number changes or is reassigned.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Message Frequency</h2>
				<p>Message frequency varies based on login activity, identity-verification requests, account-security events, assignments, status changes, reminders, and other work-related events.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Message and Data Charges</h2>
				<p>Message and data rates may apply.</p>
				<p>Users are responsible for charges imposed by their mobile carrier. Claim Consultant Group does not charge a separate fee for participation in the SMS program.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Re-Enrolling</h2>
				<p>A user who has opted out may re-enroll through an available CCG enrollment process or by contacting Claim Consultant Group. Additional affirmative consent may be required before messages resume.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Help and Support</h2>
				<p>Reply <span className={classes.keyword}>HELP</span> for assistance.</p>
				<p>Users may also contact Claim Consultant Group at:</p>
				<p>
					Email: <a href="mailto:info@claimconsultantgroup.com">info@claimconsultantgroup.com</a><br />
					Phone: <a href="tel:8886451480">(888) 645-1480</a>
				</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Message Delivery</h2>
				<p>Mobile carriers are not liable for delayed or undelivered messages.</p>
				<p>Message delivery may be affected by carrier availability, wireless coverage, network conditions, device compatibility, device settings, blocked-number settings, or other circumstances outside Claim Consultant Group’s control.</p>
				<p>Claim Consultant Group does not guarantee that every message will be delivered or received within a particular period.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>User Responsibilities</h2>
				<p>Authorized CCG users (including employees and contractors) participating in the SMS program are responsible for:</p>
				<ul>
					<li>Providing an accurate and current mobile number;</li>
					<li>Maintaining reasonable security over their mobile device;</li>
					<li>Protecting one-time passcodes and account information;</li>
					<li>Not sharing verification codes with unauthorized persons;</li>
					<li>Promptly reporting a lost, stolen, or compromised device; and</li>
					<li>Notifying Claim Consultant Group if the mobile number is disconnected, transferred, or reassigned.</li>
				</ul>
			</section>

			<section className={classes.legalSection}>
				<h2>Privacy</h2>
				<p>Mobile information is collected, used, protected, and retained according to the Claim Consultant Group <Link to="/sms-privacy">SMS Privacy Policy</Link>.</p>
				<p>We do not share, sell, or provide your mobile phone number or messaging consent data to third parties or affiliates for marketing or promotional purposes.</p>
				<p>Text messaging originator opt-in data and consent will not be shared with any third parties.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Changes or Discontinuation</h2>
				<p>Claim Consultant Group may modify, suspend, or discontinue the SMS program or these terms when reasonably necessary.</p>
				<p>The current SMS Terms and Conditions will be posted on this webpage with their effective date. When required, Claim Consultant Group will obtain additional consent before materially changing the types of SMS messages participants have agreed to receive.</p>
			</section>

			<section className={classes.legalSection}>
				<h2>Contact Information</h2>
				<p>Questions regarding the Claim Consultant Group SMS program or these terms may be directed to:</p>
				<address>
					Claim Consultant Group, LLC<br />
					308 S. Meadowlark Street<br />
					Lakeway, Texas 78734<br />
					Email: <a href="mailto:info@claimconsultantgroup.com">info@claimconsultantgroup.com</a><br />
					Phone: <a href="tel:8886451480">(888) 645-1480</a>
				</address>
			</section>
		</div>
	)
}
